import type React from 'react';

import { cn } from '@change/ds-utils';

import { withI18nPlaceholders } from '@change-corgi/core/react/i18n';

import I18N_PLACEHOLDERS from 'config/i18n/placeholders/starterDashboardV2/dynamic-tasks.yml';

import { ModuleSeparator } from 'src/app/pages/starterDashboardV2/shared/components';
import { useDynamicModules } from 'src/app/pages/starterDashboardV2/shared/contexts';
import { usePetitionAiGuidanceExperiment } from 'src/app/pages/starterDashboardV2/shared/hooks';

import { NextSteps } from '../NextSteps';
import { DynamicTasks as DynamicTasksBase } from '../NextSteps/components/DynamicTasks';

import { type AnimationPhase, useAnimationState } from './hooks/useAnimationState';

const DynamicTasks = withI18nPlaceholders(I18N_PLACEHOLDERS, DynamicTasksBase);

type NextStepsSectionProps = {
	position: 'top' | 'bottom';
	shouldShow: boolean;
	allTasksCompleted: boolean;
};

type AnimatedWrapperProps = {
	children: React.ReactNode;
	position: 'top' | 'bottom';
	phase: AnimationPhase;
};

const AnimatedWrapper: React.FC<AnimatedWrapperProps> = ({ children, position, phase }) => {
	const isEntering = phase === 'entering';
	const isExiting = phase === 'exiting';

	const getTranslateClass = () => {
		if (position === 'top' && isExiting) return 'motion-safe:translate-y-5';
		if (position === 'bottom' && isEntering) return 'motion-safe:-translate-y-5';
		return 'motion-safe:translate-y-0';
	};

	const getOpacityClass = () => {
		if (isExiting || (position === 'bottom' && isEntering)) return 'motion-safe:opacity-0';
		return 'motion-safe:opacity-100';
	};

	return (
		<div
			className={cn(
				'motion-safe:transition-all motion-safe:duration-500 motion-safe:ease-in-out',
				getTranslateClass(),
				getOpacityClass(),
			)}
		>
			{children}
		</div>
	);
};

export const NextStepsSection: React.FC<NextStepsSectionProps> = ({ position, shouldShow, allTasksCompleted }) => {
	const { isDynamicModuleLoaded } = useDynamicModules();
	const { isRendered, phase } = useAnimationState(shouldShow);
	const {
		data: { isEnabled: isDynamicTasksEnabled },
	} = usePetitionAiGuidanceExperiment();

	if (isDynamicTasksEnabled) {
		if (position === 'bottom') return null;

		return (
			<>
				<DynamicTasks />
				<ModuleSeparator data-testid="dynamic-next-steps-separator" />
			</>
		);
	}

	const renderContent = () => (
		<AnimatedWrapper position={position} phase={phase}>
			<NextSteps />
		</AnimatedWrapper>
	);

	if (position === 'top') {
		return (
			<>
				{isRendered && renderContent()}
				{!allTasksCompleted && isDynamicModuleLoaded('NextSteps') && (
					<ModuleSeparator data-testid="next-steps-separator" />
				)}
			</>
		);
	}

	return (
		<>
			{allTasksCompleted && isDynamicModuleLoaded('NextSteps') && (
				<ModuleSeparator data-testid="next-steps-bottom-separator" />
			)}
			{isRendered && renderContent()}
		</>
	);
};
